import React, { useContext, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  CloudProviderContext,
  shouldShowCloudProviderToggle,
  getImplicitCloudProviderWhenToggleHidden,
} from "../../../CloudProviderContext";
import { AwsLogoMark, AzureLogoMark } from "../../Common/CloudProviderMark";
import { useAccounts } from "../../Common/HelperComponents/GetDataFromStore";
import { isRouteDisabledForAzure } from "../../VerticalLayout/SidebarMenuOptions";

const CLOUD_PROVIDERS = [
  { id: "AWS", label: "AWS", Logo: AwsLogoMark },
  { id: "AZURE", label: "Azure", Logo: AzureLogoMark },
];

const CloudProviderToggle = () => {
  const {
    selectedProvider,
    setSelectedProvider,
    registerObservedProviders,
    showCloudProviderToggle,
  } = useContext(CloudProviderContext);
  const accounts = useAccounts();
  const accountsLoading = useSelector((state) => state.orgAccount.loading);
  const history = useHistory();
  const location = useLocation();

  // Same pool as /accounts API, minus init/test accounts
  const accountsForToggle = useMemo(() => {
    if (!Array.isArray(accounts)) return [];
    return accounts.filter(
      (account) =>
        !account.accountNumber?.includes("INIT00000001") &&
        !account.accountNumber?.includes("TEST")
    );
  }, [accounts]);

  // Observe providers for the active org only (context resets on org switch)
  useEffect(() => {
    if (typeof registerObservedProviders === "function") {
      registerObservedProviders(accountsForToggle);
    }
  }, [accountsForToggle, registerObservedProviders]);

  const showToggleFromCurrentAccounts = useMemo(
    () => shouldShowCloudProviderToggle(accountsForToggle),
    [accountsForToggle]
  );

  // Within the same org, keep toggle visible across provider-scoped refetches
  const showToggle = showCloudProviderToggle || showToggleFromCurrentAccounts;

  const implicitProvider = useMemo(
    () => getImplicitCloudProviderWhenToggleHidden(accountsForToggle),
    [accountsForToggle]
  );

  // When toggle is hidden, drive view from the single explicit type (or AWS default).
  // Skip while accounts are unloaded: an empty list implies AWS via
  // getImplicitCloudProviderWhenToggleHidden and would wipe a restored Azure
  // selection on full page reload (loading starts as null, accounts empty).
  useEffect(() => {
    if (accountsLoading || showToggle) return;
    if (accountsForToggle.length === 0) return;
    if (implicitProvider !== selectedProvider) {
      setSelectedProvider(implicitProvider);
    }
  }, [
    accountsLoading,
    showToggle,
    accountsForToggle.length,
    implicitProvider,
    selectedProvider,
    setSelectedProvider,
  ]);

  // Audit Hub stays closed on Azure even when the page is opened directly.
  useEffect(() => {
    if (selectedProvider !== "AZURE") return undefined;
    const path = location.pathname.toLowerCase();
    if (path === "/audit-hub" || path.startsWith("/audit-hub/")) {
      history.replace("/dashboard");
    }
    return undefined;
  }, [selectedProvider, location.pathname, history]);

  const handleProviderChange = (providerId) => {
    if (providerId === selectedProvider) return;
    setSelectedProvider(providerId);
    // Azure-disabled sections (e.g. Rules & Alerts, Audit Hub, ARN Insight) → Dashboard
    if (providerId === "AZURE" && isRouteDisabledForAzure(location.pathname)) {
      history.push("/dashboard");
      return;
    }
    // Deep Compliance views (e.g. /compliance/controls) stay on stale cloud data —
    // return to Compliance landing so the new cloudType can load cleanly.
    if (location.pathname.startsWith("/compliance/")) {
      history.push("/compliance");
    }
  };

  if (!showToggle) {
    return null;
  }

  return (
    <div
      className="header-cloud-toggle d-none d-lg-inline-flex"
      role="group"
      aria-label="Cloud provider"
    >
      {CLOUD_PROVIDERS.map((provider) => {
        const isActive = selectedProvider === provider.id;
        const Logo = provider.Logo;

        return (
          <button
            key={provider.id}
            type="button"
            className={`header-cloud-toggle-btn${isActive ? " active" : ""}`}
            data-provider={provider.id}
            onClick={() => handleProviderChange(provider.id)}
            aria-pressed={isActive}
            aria-label={provider.label}
            title={provider.label}
          >
            <Logo
              className={`header-cloud-toggle-logo header-cloud-toggle-logo--${provider.id === "AWS" ? "aws" : "azure"}`}
            />
          </button>
        );
      })}
    </div>
  );
};

export default CloudProviderToggle;
